/**
 * Dashboard: counters from the stats endpoint, last-7-days call chart,
 * latest calls and service status.
 */

import { useEffect, useState, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useGetIdentity, useGetList, useTranslate } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Business as OrganizationIcon,
  SupportAgent as StaffIcon,
  QrCode2 as QrCodeIcon,
  History as CallHistoryIcon,
  AccountTree as DepartmentIcon,
  PersonOutline as ClientIcon,
  MonitorHeartOutlined as HealthIcon,
  DesktopWindowsOutlined as DesktopWindowIcon,
} from '@mui/icons-material';
import { httpClient } from '@/utils/httpClient';
import { canAccessFeature, isAdmin } from '@/utils/access';
import { getRoleId } from '@/utils/auth';
import { ROLE_IDS } from '@/config/constants';
import type { ApiEnvelope } from '@/types/api';

interface HealthInfo {
  status: string;
  uptimeSeconds: number;
  calls?: { activeRooms?: number; queuedCalls?: number };
}

interface StatsInfo {
  users?: number;
  organizations?: number;
  staffs?: number;
  clients?: number;
  qrcodes?: number;
  plans?: number;
  departments?: number;
}

const formatStoredDateTime = (value: unknown): string => {
  const iso = String(value ?? '');
  if (iso.length < 16) return '';
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)} ${iso.slice(11, 16)}`;
};

const localDay = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const lastSevenDays = (): string[] => {
  const days: string[] = [];
  for (let i = 6; i >= 0; i -= 1) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    days.push(localDay(date));
  }
  return days;
};

const KpiCard = ({
  icon,
  label,
  value,
  to,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  to?: string;
}) => {
  const content = (
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', '& svg': { fontSize: 34 } }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </CardContent>
  );
  return to ? (
    <Card component={Link} to={to} sx={{ textDecoration: 'none', display: 'block', height: '100%' }}>
      {content}
    </Card>
  ) : (
    <Card sx={{ height: '100%' }}>{content}</Card>
  );
};

const CallsChart = ({ counts }: { counts: { day: string; total: number }[] }) => {
  const translate = useTranslate();
  const max = Math.max(1, ...counts.map((c) => c.total));
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, height: 140, mt: 1 }}>
      {counts.map(({ day, total }) => (
        <Tooltip key={day} title={`${day.slice(8, 10)}/${day.slice(5, 7)}: ${total}`}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, height: '100%', justifyContent: 'flex-end' }}>
            <Typography variant="caption">{total}</Typography>
            <Box
              sx={{
                width: '100%',
                maxWidth: 48,
                height: `${(total / max) * 100}%`,
                minHeight: 2,
                backgroundColor: 'primary.main',
                borderRadius: '4px 4px 0 0',
                opacity: total === 0 ? 0.25 : 0.9,
              }}
              aria-label={translate('pages.dashboard.chart_bar', { day, total, _: `${day}: ${total}` })}
            />
            <Typography variant="caption" color="text.secondary">
              {day.slice(8, 10)}/{day.slice(5, 7)}
            </Typography>
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
};

// Staff (role 3) have no management screens of their own — they use the
// softphone, not the panel. This card is informational only: the single
// entry point to the softphone is the app bar's "Recibir llamadas" button
// (see CustomAppBar.tsx); this card just tells staff what that button opens
// and where to find the one-time setup (install/automatic start) once
// they're inside the softphone. It is gated directly on the role rather
// than `canAccessFeature` (which always lets admin through, and this card
// is not meant for admin).
const DesktopWindowCard = () => {
  const translate = useTranslate();
  return (
    <Card>
      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', '& svg': { fontSize: 34 } }}>
          <DesktopWindowIcon />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {translate('pages.dashboard.desktop_window_title', {
              _: 'Desktop window for calls',
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('pages.dashboard.desktop_window_body', {
              _: 'The softphone can be installed as its own desktop window: it announces calls with the window minimised, gets its own taskbar icon and, if you choose to, can start automatically when the computer starts. To open it, use the "Receive calls" button in the top bar. Inside, on the staff screen, you will find the "Install application" and "Enable automatic start" options.',
            })}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  const admin = isAdmin();
  const staff = getRoleId() === ROLE_IDS.STAFF;
  const [health, setHealth] = useState<HealthInfo | null>(null);
  const [stats, setStats] = useState<StatsInfo | null>(null);

  const days = lastSevenDays();
  const { data: weekCalls } = useGetList('callDetails', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'start_time', order: 'DESC' },
    filter: { date_from: days[0], date_to: days[6] },
  });
  const { data: recentCalls } = useGetList('callDetails', {
    pagination: { page: 1, perPage: 5 },
    sort: { field: 'start_time', order: 'DESC' },
  });

  // Non-admins get their own tenant-scoped counters (the /dashboard/statsinfo
  // endpoint reports global totals, so it is admin-only here). The backend
  // scopes each of these lists to the caller's organization(s).
  const idSort = { field: 'id', order: 'DESC' as const };
  const onePage = { page: 1, perPage: 1 };
  const { total: orgTotal } = useGetList('organizations', { pagination: onePage, sort: idSort });
  const { total: staffTotal } = useGetList('staff', { pagination: onePage, sort: idSort });
  const { total: qrTotal } = useGetList('qrcodes', { pagination: onePage, sort: idSort });
  const { total: deptTotal } = useGetList('departments', { pagination: onePage, sort: idSort });

  useEffect(() => {
    // Global counters are admin-only; non-admins derive counts from their
    // scoped resource lists above.
    if (admin) {
      httpClient
        .get<ApiEnvelope<StatsInfo>>('/dashboard/statsinfo')
        .then((response) => setStats(response.data.data ?? null))
        .catch(() => setStats(null));
    }
    httpClient
      .get<HealthInfo>('/health', { skipAuth: true })
      .then((response) => setHealth(response.data))
      .catch(() => setHealth(null));
  }, [admin]);

  const chartCounts = days.map((day) => ({
    day,
    total: (weekCalls ?? []).filter((call: any) => String(call.start_time ?? '').slice(0, 10) === day).length,
  }));

  // KPI cards, gated by feature access; admin reads global stats, a tenant
  // reads its own scoped list totals.
  const allKpis = [
    { feature: 'organizations', icon: <OrganizationIcon />, value: admin ? stats?.organizations : orgTotal, label: translate('resources.organizations.name', { smart_count: 2 }), to: '/organizations' },
    { feature: 'staff', icon: <StaffIcon />, value: admin ? stats?.staffs : staffTotal, label: translate('resources.staff.name', { smart_count: 2 }), to: '/staff' },
    { feature: 'qrcodes', icon: <QrCodeIcon />, value: admin ? stats?.qrcodes : qrTotal, label: translate('resources.qrcodes.name', { smart_count: 2 }), to: '/qrcodes' },
    { feature: 'departments', icon: <DepartmentIcon />, value: admin ? stats?.departments : deptTotal, label: translate('resources.departments.name', { smart_count: 2 }), to: '/departments' },
    { feature: 'clients', icon: <ClientIcon />, value: stats?.clients, label: translate('pages.dashboard.clients'), to: undefined },
    { feature: 'callDetails', icon: <CallHistoryIcon />, value: chartCounts[6]?.total ?? '—', label: translate('pages.dashboard.calls_today'), to: '/callDetails' },
  ];
  const kpis = allKpis.filter((kpi) => canAccessFeature(kpi.feature));

  // With few KPI tiles (a staff member only sees "calls today") a dedicated KPI
  // row reads as a lonely card floating over an empty band. In that case the
  // tiles stack in the right-hand column above the service status, and the
  // chart takes the left column - every card lands in a balanced two-column
  // block. Admin/tenant views with a full tile set keep the classic KPI row.
  const stackKpisAside = kpis.length <= 2;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h5">
        {translate('pages.dashboard.welcome', { name: identity?.fullName ?? '', _: 'QR Phone' })}
      </Typography>

      {staff && <DesktopWindowCard />}

      {!stackKpisAside && (
        <Grid container spacing={2}>
          {kpis.map((kpi) => (
            <Grid item xs={12} sm={6} md={2} key={kpi.label}>
              <KpiCard icon={kpi.icon} value={kpi.value ?? '—'} label={kpi.label} to={kpi.to} />
            </Grid>
          ))}
        </Grid>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={stackKpisAside ? 8 : 7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {translate('pages.dashboard.calls_week')}
              </Typography>
              <CallsChart counts={chartCounts} />
            </CardContent>
          </Card>
        </Grid>
        <Grid
          item
          xs={12}
          md={stackKpisAside ? 4 : 5}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {stackKpisAside &&
            kpis.map((kpi) => (
              <KpiCard
                key={kpi.label}
                icon={kpi.icon}
                value={kpi.value ?? '—'}
                label={kpi.label}
                to={kpi.to}
              />
            ))}
          <Card sx={{ flexGrow: 1 }}>
            <CardContent>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <HealthIcon fontSize="small" />
                {translate('pages.dashboard.service_status')}
              </Typography>
              {health ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Chip
                    size="small"
                    color={health.status === 'ok' ? 'success' : 'error'}
                    label={
                      health.status === 'ok'
                        ? translate('pages.dashboard.service_ok')
                        : translate('pages.dashboard.service_down')
                    }
                    sx={{ width: 'fit-content' }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {translate('pages.dashboard.active_calls')}: {health.calls?.activeRooms ?? 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {translate('pages.dashboard.queued_calls')}: {health.calls?.queuedCalls ?? 0}
                  </Typography>
                </Box>
              ) : (
                <Chip size="small" color="error" label={translate('pages.dashboard.service_down')} />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            {translate('pages.dashboard.recent_calls')}
          </Typography>
          {recentCalls?.length ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{translate('resources.callDetails.fields.start_time')}</TableCell>
                  <TableCell>{translate('resources.callDetails.fields.caller')}</TableCell>
                  <TableCell>{translate('resources.callDetails.fields.user_name')}</TableCell>
                  <TableCell>{translate('resources.callDetails.fields.callstatus')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentCalls.map((call: any) => (
                  <TableRow key={call.id}>
                    <TableCell>{formatStoredDateTime(call.start_time)}</TableCell>
                    <TableCell>{call.caller}</TableCell>
                    <TableCell>{call.user_name}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={call.isCallConnected ? 'success' : 'default'}
                        label={translate(
                          `resources.callDetails.connection.${call.isCallConnected ? 'yes' : 'no'}`
                        )}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {translate('pages.dashboard.no_data')}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;
